import type { RouteObject } from "react-router";
import { RequireAuth, RequireRole } from "@/auth/require-auth";
import { AccountPage } from "@/pages/account";
import { ApiKeysPage } from "@/pages/api-keys";
import { AuditPage } from "@/pages/audit";
import { BillingPage } from "@/pages/billing";
import { CmsMediaPage } from "@/pages/cms-media";
import { CmsPageDesignPage, CmsPageEditPage, CmsPagesPage, CmsPostsPage } from "@/pages/cms-pages";
import { CmsPricingPage } from "@/pages/cms-pricing";
import { CmsSitePage } from "@/pages/cms-site";
import { DashboardPage } from "@/pages/dashboard";
import { DevicesPage } from "@/pages/devices";
import { ForbiddenPage, NotFoundPage, RouteErrorPage } from "@/pages/errors";
import { LicenseRedirectPage, LicensesPage } from "@/pages/licenses";
import { LoginPage } from "@/pages/login";
import { MailSettingsPage } from "@/pages/mail-settings";
import { OrgDetailLayout, OrgTab } from "@/pages/org-detail";
import { OrgNewPage } from "@/pages/org-new";
import { OrgsPage } from "@/pages/orgs";
import { ReleaseDetailPage } from "@/pages/release-detail";
import { ReleasesPage } from "@/pages/releases";
import { RolesPage } from "@/pages/roles";
import { SsoSettingsPage } from "@/pages/sso-settings";
import { StaffPage } from "@/pages/staff";
import { TelemetryPage } from "@/pages/telemetry";
import { FtaReadinessPage } from "@/pages/fta-readiness";
import { Shell } from "./shell";

/** Route table from the plan (Slice D → apps/admin). */
export const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorPage />,
    children: [
      // Full-screen page designer: no Shell chrome, still behind auth + cms:write.
      {
        element: <RequireRole perm="cms:write" />,
        children: [{ path: "cms/pages/:id/design", element: <CmsPageDesignPage /> }],
      },
      {
        element: <Shell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "403", element: <ForbiddenPage /> },
          { path: "account", element: <AccountPage /> },
          {
            element: <RequireRole perm="readiness:read" />,
            children: [{ path: "fta-readiness", element: <FtaReadinessPage /> }],
          },
          {
            element: <RequireRole perm="orgs:read" />,
            children: [
              { path: "orgs", element: <OrgsPage /> },
              { path: "orgs/new", element: <OrgNewPage /> },
              {
                path: "orgs/:id",
                element: <OrgDetailLayout />,
                children: [
                  { index: true, element: <OrgTab /> },
                  { path: ":tab", element: <OrgTab /> },
                ],
              },
            ],
          },
          {
            element: <RequireRole perm="licenses:read" />,
            children: [
              { path: "licenses", element: <LicensesPage /> },
              { path: "licenses/:id", element: <LicenseRedirectPage /> },
            ],
          },
          {
            element: <RequireRole perm="devices:read" />,
            children: [{ path: "devices", element: <DevicesPage /> }],
          },
          {
            element: <RequireRole perm="telemetry:read" />,
            children: [{ path: "telemetry", element: <TelemetryPage /> }],
          },
          {
            element: <RequireRole perm="releases:read" />,
            children: [
              { path: "releases", element: <ReleasesPage /> },
              { path: "releases/:version", element: <ReleaseDetailPage /> },
            ],
          },
          {
            element: <RequireRole perm="billing:read" />,
            children: [{ path: "billing", element: <BillingPage /> }],
          },
          {
            element: <RequireRole perm="cms:read" />,
            children: [
              { path: "cms/pages", element: <CmsPagesPage /> },
              { path: "cms/pages/:id", element: <CmsPageEditPage /> },
              { path: "cms/pricing", element: <CmsPricingPage /> },
              { path: "cms/posts", element: <CmsPostsPage /> },
              { path: "cms/media", element: <CmsMediaPage /> },
              { path: "cms/site", element: <CmsSitePage /> },
            ],
          },
          {
            element: <RequireRole perm="staff:read" />,
            children: [
              { path: "staff", element: <StaffPage /> },
              { path: "settings/roles", element: <RolesPage /> },
            ],
          },
          {
            element: <RequireRole perm="audit:read" />,
            children: [{ path: "settings/audit", element: <AuditPage /> }],
          },
          {
            element: <RequireRole perm="apikeys:write" />,
            children: [{ path: "settings/api-keys", element: <ApiKeysPage /> }],
          },
          {
            element: <RequireRole perm="staff:write" />,
            children: [
              { path: "settings/sso", element: <SsoSettingsPage /> },
              { path: "settings/email", element: <MailSettingsPage /> },
            ],
          },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
